import { useEffect, useMemo, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { useQueryList } from '@percent/utility'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'

const NONPROFIT_SEARCH_DEBOUNCE_MS = 300
const MIN_DEBOUNCED_QUERY_LENGTH = 3

export function useNonprofitLookup(countryCode: string) {
  const { nonprofitsService } = useServices()
  const [searchQuery, setSearchQuery] = useState('')
  const [lastSearchedCountry, setLastSearchedCountry] = useState('')

  const [debouncedQuery] = useDebounceValue(searchQuery, NONPROFIT_SEARCH_DEBOUNCE_MS)

  const shouldSearch = debouncedQuery.length >= MIN_DEBOUNCED_QUERY_LENGTH

  const [{ data: searchData, isLoading }] = useQueryList(
    ['getWorkplaceOrganisations', debouncedQuery, countryCode],
    nonprofitsService.getWorkplaceOrganisations,
    { query: debouncedQuery, pageSize: 10, ...(countryCode ? { countryCode } : {}) },
    { enabled: shouldSearch, placeholderData: undefined }
  )

  useEffect(() => {
    setLastSearchedCountry('')
  }, [countryCode])

  useEffect(() => {
    if (shouldSearch) {
      setLastSearchedCountry(countryCode)
    }
  }, [shouldSearch, countryCode])

  const options = useMemo(
    () =>
      countryCode === lastSearchedCountry
        ? (searchData ?? []).map(org => ({ value: org.organisationId, label: org.name }))
        : [],
    [searchData, countryCode, lastSearchedCountry]
  )

  return {
    searchQuery,
    setSearchQuery,
    options,
    isLoading
  }
}
